/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axiosClient';
import { useTheme } from '../../context/ThemeContext';
import { useBookingDraft } from '../../context/PortalContext';
import { usePortalSession } from '../../context/PortalContext';
import toast from '../../utils/toast';
import { motion } from 'framer-motion';
import ProfileAvatar from '../shared/ProfileAvatar';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const BarberCard = ({ barber, isSelected, onSelect, onTogglePreferred }) => {
  const { isDark } = useTheme();
  const { portalData } = usePortalSession();
  const isPreferred = portalData?.client?.preferred_barber_user_id === barber.id;

  return (
    <motion.div
      className={`p-5 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
        isSelected
          ? 'border-[--accent] bg-gradient-to-br from-[--accent]/10 to-[--accent]/5 shadow-lg shadow-[--accent]/20'
          : 'border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 hover:bg-white/70 dark:hover:bg-zinc-800/70 backdrop-blur-sm'
      }`}
      onClick={() => onSelect(barber.id)}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      style={{ '--accent': ACCENT }}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          {/* Barber avatar — real photo when available, initials fallback */}
          {barber.avatar_url ? (
            <div
              className="w-14 h-14 rounded-2xl overflow-hidden shadow-lg flex-shrink-0"
              style={{ boxShadow: isSelected ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.2)' }}
            >
              <ProfileAvatar
                name={barber.name}
                avatarUrl={barber.avatar_url}
                size="lg"
                className="rounded-2xl w-full h-full"
                onClick={(event) => {
                  event.stopPropagation();
                }}
                previewable
              />
            </div>
          ) : (
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg flex-shrink-0"
              style={{
                background: isSelected
                  ? `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}CC 100%)`
                  : barber.color_hex || ACCENT,
                boxShadow: isSelected ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.2)'
              }}
            >
              {barber.name.charAt(0).toUpperCase()}
            </div>
          )}
          
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-zinc-900 dark:text-zinc-100">
              {barber.name}
            </h3>
            
            {/* Enhanced Specialties */}
            {barber.specialties && barber.specialties.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {barber.specialties.map((specialty, index) => (
                  <motion.span
                    key={index}
                    className={`px-3 py-1 text-xs font-medium rounded-full ${
                      isSelected
                        ? 'bg-[--accent]/20 text-[--accent] border border-[--accent]/30'
                        : 'bg-white/50 dark:bg-zinc-800/50 text-zinc-600 dark:text-zinc-300 border border-white/20 dark:border-white/10'
                    }`}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                    style={{ '--accent': ACCENT }}
                  >
                    {specialty}
                  </motion.span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Preferred toggle */}
        <motion.button
          onClick={(e) => {
            e.stopPropagation();
            onTogglePreferred(barber.id);
          }}
          className={`p-3 rounded-xl transition-all duration-200 ${
            isPreferred
              ? 'text-yellow-500 hover:text-yellow-400 bg-yellow-500/10 hover:bg-yellow-500/20'
              : 'text-zinc-400 hover:text-zinc-300 hover:bg-white/20 dark:hover:bg-zinc-700/50'
          }`}
          title={isPreferred ? 'Remove as preferred' : 'Set as preferred'}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <svg className="h-6 w-6" fill={isPreferred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        </motion.button>
      </div>
    </motion.div>
  );
};

const ServiceCard = React.memo(({ service, isSelected, onSelect }) => {
  const { isDark } = useTheme();

  return (
    <motion.div
      className={`p-5 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
        isSelected
          ? 'border-[--accent] bg-gradient-to-br from-[--accent]/10 to-[--accent]/5 shadow-lg shadow-[--accent]/20'
          : 'border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 hover:bg-white/70 dark:hover:bg-zinc-800/70 backdrop-blur-sm'
      }`}
      onClick={() => onSelect(service.id)}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      style={{ '--accent': ACCENT }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            {/* Service Icon */}
            <div 
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isSelected
                  ? 'bg-[--accent]/20 text-[--accent]'
                  : 'bg-white/50 dark:bg-zinc-800/50 text-zinc-600 dark:text-zinc-400'
              }`}
              style={{ '--accent': ACCENT }}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg text-zinc-900 dark:text-zinc-100">
                {service.name}
              </h3>
              
              <div className="text-sm font-medium text-zinc-600 dark:text-zinc-300">
                {service.duration_min} minutes
              </div>
            </div>
          </div>
          
          {service.description && (
            <p className="text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
              {service.description}
            </p>
          )}
        </div>

        {/* Enhanced Selection indicator */}
        {isSelected && (
          <motion.div 
            className="ml-3"
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
          >
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
              style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}CC 100%)` }}
            >
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
});

// Service Preview Card (Read-only, shows all services)
const ServicePreviewCard = React.memo(({ service, isEnabled, isSelected }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');

  return (
    <motion.div
      className={`p-4 rounded-xl border transition-all duration-300 ${
        isSelected
          ? 'border-[--accent] bg-gradient-to-br from-[--accent]/10 to-[--accent]/5 shadow-md shadow-[--accent]/10'
          : isEnabled
          ? 'border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm'
          : 'border-white/10 dark:border-white/5 bg-white/30 dark:bg-zinc-800/30 backdrop-blur-sm opacity-50'
      }`}
      animate={{ opacity: 1 }}
      style={{ '--accent': ACCENT }}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3 flex-1">
          {/* Service Icon */}
          <div 
            className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isSelected
                ? 'bg-[--accent]/20 text-[--accent]'
                : isEnabled
                ? 'bg-white/50 dark:bg-zinc-800/50 text-zinc-600 dark:text-zinc-400'
                : 'bg-white/30 dark:bg-zinc-700/30 text-zinc-400 dark:text-zinc-500'
            }`}
            style={{ '--accent': ACCENT }}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className={`font-semibold text-sm ${
              isEnabled 
                ? 'text-zinc-900 dark:text-zinc-100' 
                : 'text-zinc-500 dark:text-zinc-400'
            }`}>
              {service.name}
            </h4>
            
            <div className="flex items-center gap-3 mt-1">
              <div className={`text-xs font-medium ${
                isEnabled 
                  ? 'text-zinc-600 dark:text-zinc-300' 
                  : 'text-zinc-400 dark:text-zinc-500'
              }`}>
                {service.duration_min} min
              </div>
              
              {service.price_cents > 0 && (
                <div className={`text-xs font-semibold ${
                  isEnabled 
                    ? 'text-[--accent]' 
                    : 'text-zinc-400 dark:text-zinc-500'
                }`} style={{ '--accent': ACCENT }}>
                  ${(service.price_cents / 100).toFixed(2)}
                </div>
              )}
            </div>

            {!isEnabled && (
              <div className="text-xs text-zinc-400 dark:text-zinc-500 mt-1 italic">
                {t('booking.select_barber_to_see_availability')}
              </div>
            )}
          </div>
        </div>

        {/* Selection indicator */}
        {isSelected && (
          <motion.div 
            className="ml-2"
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
          >
            <div 
              className="w-6 h-6 rounded-full flex items-center justify-center shadow-md"
              style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}CC 100%)` }}
            >
              <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
});

// Service Preview Panel Component
const ServicePreviewPanel = React.memo(({ selectedBarberId, selectedServiceId, availableServiceIds }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const publicBookingId = (() => {
    try { return localStorage.getItem('public_booking_id'); } catch { return null; }
  })();

  useEffect(() => {
    // Only load once on mount
    let mounted = true;
    
    const loadAllServices = async () => {
      try {
        const response = publicBookingId
          ? await axios.get(`/public/booking/${publicBookingId}`)
          : await axios.get('/portal/services');
        if (mounted) {
          setAllServices(response.data.services || []);
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to load services for preview:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadAllServices();
    
    return () => {
      mounted = false;
    };
  }, [publicBookingId]); // Reload if public booking mode changes

  if (loading) {
    return (
      <Card className="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
            {t('booking.our_services')}
          </h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
            {t('booking.our_services_subtitle')}
          </p>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse h-24 bg-zinc-200 dark:bg-zinc-700 rounded-xl"></div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 h-fit sticky top-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
          {t('booking.our_services')}
        </h2>
        <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
          {selectedBarberId
            ? t('booking.services_from_barber')
            : t('booking.select_barber_for_services')}
        </p>
      </div>

      {allServices.length === 0 ? (
        <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">
          <svg className="h-12 w-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <p className="text-sm">{t('booking.no_services_available')}</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-zinc-300 dark:scrollbar-thumb-zinc-600 scrollbar-track-transparent">
          {allServices.map(service => (
            <ServicePreviewCard
              key={service.id}
              service={service}
              isEnabled={selectedBarberId && availableServiceIds.includes(service.id)}
              isSelected={service.id === selectedServiceId}
            />
          ))}
        </div>
      )}
    </Card>
  );
});

const BarberSelection = ({ selectedBarberId, onBarberSelect, onTogglePreferred, portalData, draft, handleBarberSelect }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const autoSelectedRef = useRef(false);
  const publicBookingId = (() => {
    try { return localStorage.getItem('public_booking_id'); } catch { return null; }
  })();

  useEffect(() => {
    const loadBarbers = async () => {
      try {
        setLoading(true);
        let barbersList = [];
        if (publicBookingId) {
          const response = await axios.get(`/public/booking/${publicBookingId}`);
          barbersList = response.data.barbers || [];
        } else {
          const response = await axios.get('/portal/barbers');
          barbersList = response.data.barbers;
        }
        setBarbers(barbersList);
      } catch (error) {
        console.error('Failed to load barbers:', error);
        toast.error('Failed to load barbers');
      } finally {
        setLoading(false);
      }
    };

    loadBarbers();
  }, [publicBookingId]);

  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (!portalData?.client?.preferred_barber_user_id) return;
    if (draft.barber_user_id) return;
    if (barbers.length === 0) return;

    const preferredBarber = barbers.find(
      b => b.id === portalData.client.preferred_barber_user_id
    );
    if (preferredBarber) {
      autoSelectedRef.current = true;
      handleBarberSelect(preferredBarber.id);
    }
  }, [barbers, portalData, draft.barber_user_id, handleBarberSelect]);

  const filteredBarbers = barbers.filter(barber =>
    barber.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (barber.specialties && barber.specialties.some(specialty =>
      specialty.toLowerCase().includes(searchQuery.toLowerCase())
    ))
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-6 w-40 bg-gray-300 dark:bg-gray-600 rounded-lg mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className={`h-24 bg-gray-200 dark:bg-gray-700 rounded-2xl`}></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold mb-2 text-zinc-900 dark:text-zinc-100">
          {t('booking.choose_barber')}
        </h2>
        <p className="text-sm text-zinc-600 dark:text-zinc-400">
          {t('booking.choose_barber_subtitle')}
        </p>
        
        {/* Enhanced Search */}
        <div className="mt-4">
          <div className="relative">
            <input
              type="text"
              placeholder={t('booking.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 pl-12 rounded-xl border transition-all duration-200 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 dark:placeholder-zinc-400 focus:border-[--accent] focus:ring-2 focus:ring-[--accent]/20 focus:bg-white/70 dark:focus:bg-zinc-800/70 backdrop-blur-sm"
              style={{ '--accent': ACCENT }}
            />
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-zinc-500 dark:text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {filteredBarbers.length === 0 ? (
        <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">
          <svg className="h-12 w-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <p>{t('booking.no_barbers')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBarbers.map(barber => (
            <BarberCard
              key={barber.id}
              barber={barber}
              isSelected={selectedBarberId === barber.id}
              onSelect={onBarberSelect}
              onTogglePreferred={onTogglePreferred}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ServiceSelection = React.memo(({ selectedServiceId, selectedBarberId, onServiceSelect, onServicesLoaded }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const publicBookingId = (() => {
    try { return localStorage.getItem('public_booking_id'); } catch { return null; }
  })();

  useEffect(() => {
    const loadServices = async () => {
      try {
        if (services.length === 0) {
          setLoading(true);
        } else {
          setIsRefreshing(true);
        }
        if (publicBookingId) {
          const url = selectedBarberId
            ? `/public/booking/${publicBookingId}?barber_id=${selectedBarberId}`
            : `/public/booking/${publicBookingId}`;
          const response = await axios.get(url);
          const loadedServices = response.data.services || [];
          setServices(loadedServices);
          // Pass available service IDs to parent
          if (onServicesLoaded) {
            onServicesLoaded(loadedServices.map(s => s.id));
          }
        } else {
          // Filter services by selected barber if a barber is selected
          const url = selectedBarberId 
            ? `/portal/services?barber_id=${selectedBarberId}`
            : '/portal/services';
          const response = await axios.get(url);
          const loadedServices = response.data.services;
          setServices(loadedServices);
          // Pass available service IDs to parent
          if (onServicesLoaded) {
            onServicesLoaded(loadedServices.map(s => s.id));
          }
        }
      } catch (error) {
        console.error('Failed to load services:', error);
        toast.error('Failed to load services');
        if (onServicesLoaded) {
          onServicesLoaded([]);
        }
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    };

    loadServices();
  }, [publicBookingId, selectedBarberId]); // Removed onServicesLoaded from deps to prevent re-renders

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-6 w-40 bg-gray-300 dark:bg-gray-600 rounded-lg mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className={`h-28 bg-gray-200 dark:bg-gray-700 rounded-2xl`}></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold mb-2 text-zinc-900 dark:text-zinc-100">
          {t('booking.choose_service')}
        </h2>
        <p className="text-sm text-zinc-600 dark:text-zinc-400">
          {selectedBarberId
            ? t('booking.choose_service_subtitle_selected')
            : t('booking.choose_service_subtitle_none')}
        </p>
      </div>

      {!selectedBarberId ? (
        <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">
          <svg className="h-12 w-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <p className="font-medium">{t('booking.please_select_barber')}</p>
          <p className="text-sm mt-1">{t('booking.services_appear_after_barber')}</p>
        </div>
      ) : services.length === 0 ? (
        <div className="text-center py-8 text-zinc-500 dark:text-zinc-400">
          <svg className="h-12 w-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <p>{t('booking.no_services_for_barber')}</p>
        </div>
      ) : (
        <div className={`space-y-3 transition-opacity duration-300 ${isRefreshing ? 'opacity-60' : 'opacity-100'}`}>
          {services.map(service => (
            <ServiceCard
              key={service.id}
              service={service}
              isSelected={selectedServiceId === service.id}
              onSelect={onServiceSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
});

const BookingStep1 = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');
  const { draft, updateDraft, isComplete } = useBookingDraft();
  const { portalData, updatePortalData } = usePortalSession();
  const [availableServiceIds, setAvailableServiceIds] = useState([]);

  // Clear reschedule_booking_id when starting a new booking flow
  useEffect(() => {
    if (draft.reschedule_booking_id) {
      updateDraft({ reschedule_booking_id: null });
    }
  }, []);

  const handleBarberSelect = useCallback((barberId) => {
    // Clear service selection when barber changes
    updateDraft({ barber_user_id: barberId, service_id: null });
    // Fire analytics event
    if (window.gtag) {
      window.gtag('event', 'barber_selected', {
        barber_id: barberId
      });
    }
  }, [updateDraft]);

  const handleServiceSelect = useCallback((serviceId) => {
    updateDraft({ service_id: serviceId });
    // Fire analytics event
    if (window.gtag) {
      window.gtag('event', 'service_selected', {
        service_id: serviceId
      });
    }
  }, [updateDraft]);

  const handleServicesLoaded = useCallback((serviceIds) => {
    setAvailableServiceIds(serviceIds);
  }, []);

  const handleTogglePreferred = async (barberId) => {
    try {
      const result = await updatePortalData({ preferred_barber_user_id: barberId });
      if (result.success) {
        toast.success('Preferred barber updated');
        // Fire analytics event
        if (window.gtag) {
          window.gtag('event', 'preferred_barber_updated', {
            barber_id: barberId
          });
        }
      } else {
        toast.error(result.error || 'Failed to update preferred barber');
      }
    } catch (error) {
      toast.error('Failed to update preferred barber');
    }
  };

  const handleContinue = () => {
    if (!isComplete) return;
    
    // Fire analytics event
    if (window.gtag) {
      window.gtag('event', 'booking_step1_completed', {
        barber_id: draft.barber_user_id,
        service_id: draft.service_id
      });
    }
    
    navigate('/app/book/time');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-1 text-zinc-900 dark:text-zinc-100">
          {t('booking.step1_title')}
        </h1>
        <p className="text-zinc-600 dark:text-zinc-400">
          {t('booking.step1_subtitle')}
        </p>
      </div>

      {/* Two-Column Layout: Desktop = Side-by-side, Mobile = Stacked */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT COLUMN: Service Preview Panel (Desktop: 5 cols, Mobile: Full width, appears first on mobile) */}
        <div className="lg:col-span-5 order-1 lg:order-1">
          <ServicePreviewPanel
            selectedBarberId={draft.barber_user_id}
            selectedServiceId={draft.service_id}
            availableServiceIds={availableServiceIds}
          />
        </div>

        {/* RIGHT COLUMN: Booking Flow (Desktop: 7 cols, Mobile: Full width, appears second on mobile) */}
        <div className="lg:col-span-7 order-2 lg:order-2 space-y-6">
          {/* Barber Selection */}
          <Card className="p-6">
            <BarberSelection
              selectedBarberId={draft.barber_user_id}
              onBarberSelect={handleBarberSelect}
              onTogglePreferred={handleTogglePreferred}
              portalData={portalData}
              draft={draft}
              handleBarberSelect={handleBarberSelect}
            />
          </Card>

          {/* Service Selection */}
          <Card className="p-6">
            <ServiceSelection
              selectedServiceId={draft.service_id}
              selectedBarberId={draft.barber_user_id}
              onServiceSelect={handleServiceSelect}
              onServicesLoaded={handleServicesLoaded}
            />
          </Card>
        </div>
      </div>

      {/* Continue Button */}
      <div className="mt-8 flex justify-center">
        <motion.button
          onClick={handleContinue}
          disabled={!isComplete}
          className={`px-8 py-3 rounded-xl font-medium transition-all duration-200 ${
            isComplete
              ? 'text-white shadow-lg'
              : 'bg-zinc-300 dark:bg-zinc-600 text-zinc-500 dark:text-zinc-400 cursor-not-allowed'
          }`}
          style={isComplete ? { 
            background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}CC 100%)`,
            boxShadow: `0 4px 16px ${ACCENT}40`
          } : {}}
          whileHover={isComplete ? { scale: 1.02 } : {}}
          whileTap={isComplete ? { scale: 0.98 } : {}}
        >
          {t('booking.continue_to_time')}
        </motion.button>
      </div>
    </div>
  );
};

export default BookingStep1;
